import {Input} from 'antd';
import {Fragment} from 'react';
import {useParams} from 'react-router';

import {AddButton} from '../../../../../../../layout/add-button/add-button';
import {AdditionalInfo} from '../../../../../../../layout/additional-info/additional-info';
import {useBrandValidationSettings} from '../../../../../../../provider/brands/brand-validation-settings';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {CompanyKeyEnum} from '../../../../../../../service/company-v2/company-const';
import {usePhoneDescriptionEnabled} from '../../../../../../../service/feature-flags/use-phone-description-enabled';
import {useFormRules} from '../../../../../../../service/form/form-rules-hook';
import {usePhoneCommentVisibility} from '../../../../../../../service/phone-comment-visibility/phone-comment-visibility-hook';
import {urlQueryMap} from '../../../../../../../shared-search-parameters';
import {Form} from '../../../../../../../typings/antd';
import {classNames} from '../../../../../../../util/css';
import {ProvidersIdsEnum} from '../../../../../../../util/type';
import {useUrl} from '../../../../../../../util/url-hook/url-hook';
import {ErrorList} from '../../../error-list/error-list';

import {PhonesFormItemButtons} from './phones-form-item-buttons';
import * as styles from './phones-form-item.scss';

type PropsType = {
    catalogId?: number;
    name: CompanyKeyEnum.Phones | [CompanyKeyEnum.ExtraPhones, number, 'phones'];
    required?: boolean;
    hasInitialPhones: boolean;
    brandId?: number | null;
};

type PhoneRowPropsType = {
    catalogId?: number;
    fieldKey?: number;
    fieldName: number;
    getLocalizedString: (
        key:
            | 'COMPANY_FORM__CONTACT_INFO__PHONE__PLACEHOLDER'
            | 'COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__PLACEHOLDER'
    ) => string;
    index: number;
    isPhoneRequired: boolean;
    maxLengthFieldRule: ReturnType<typeof useFormRules>['maxLengthFieldRule'];
    phoneLabel: JSX.Element;
    descriptionLabel: JSX.Element;
    requiredFieldRule: ReturnType<typeof useFormRules>['requiredFieldRule'];
    onRemove: () => void;
};

function renderPhoneRowWithoutDescription(props: PhoneRowPropsType): JSX.Element {
    const {
        catalogId,
        fieldKey,
        fieldName,
        getLocalizedString,
        index,
        isPhoneRequired,
        phoneLabel,
        requiredFieldRule,
        onRemove,
    } = props;
    const inputSize = !index ? 'large' : 'middle';

    return (
        <div className={styles.company_phone}>
            <Form.Item
                fieldKey={fieldKey}
                label={index === 0 ? phoneLabel : null}
                name={[fieldName, 'phone']}
                rules={isPhoneRequired ? [requiredFieldRule] : []}
            >
                <Input
                    placeholder={getLocalizedString('COMPANY_FORM__CONTACT_INFO__PHONE__PLACEHOLDER')}
                    size={inputSize}
                />
            </Form.Item>

            <PhonesFormItemButtons catalogId={catalogId} index={index} onRemove={onRemove} />
        </div>
    );
}

function renderPhoneRowWithDescription(props: PhoneRowPropsType): JSX.Element {
    const {
        catalogId,
        fieldKey,
        fieldName,
        getLocalizedString,
        index,
        isPhoneRequired,
        maxLengthFieldRule,
        phoneLabel,
        descriptionLabel,
        requiredFieldRule,
        onRemove,
    } = props;
    const inputSize = !index ? 'large' : 'middle';

    return (
        <div className={classNames(styles.company_phone, styles.company_phone_with_description)}>
            <div className={styles.company_phone__fields}>
                <Form.Item
                    className={styles.company_phone__phone}
                    fieldKey={fieldKey}
                    label={index === 0 ? phoneLabel : null}
                    name={[fieldName, 'phone']}
                    rules={isPhoneRequired ? [requiredFieldRule] : []}
                >
                    <Input
                        placeholder={getLocalizedString('COMPANY_FORM__CONTACT_INFO__PHONE__PLACEHOLDER')}
                        size={inputSize}
                    />
                </Form.Item>

                <Form.Item
                    className={styles.company_phone__description}
                    label={index === 0 ? descriptionLabel : null}
                    name={[fieldName, 'description']}
                    rules={[maxLengthFieldRule(50)]}
                >
                    <Input
                        placeholder={getLocalizedString('COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__PLACEHOLDER')}
                        size={inputSize}
                    />
                </Form.Item>

                <PhonesFormItemButtons catalogId={catalogId} index={index} onRemove={onRemove} />
            </div>
        </div>
    );
}

// Three gates: the feature has to be rolled out to the company's brand, the block has to be one
// that forwards the comment (the main phone block, Yandex Maps or 2GIS), and the backend has to say
// the company actually pushes to those catalogs. The backend answer is required — an unknown one
// keeps the field hidden rather than guessing.
function isPhoneDescriptionShown(options: {
    catalogId?: number;
    isFeatureAvailable: boolean;
    isVisibleForCompany?: boolean;
}): boolean {
    const {catalogId, isFeatureAvailable, isVisibleForCompany} = options;

    const isForwardingBlock =
        catalogId === undefined || // eslint-disable-line no-undefined
        catalogId === ProvidersIdsEnum.yandex ||
        catalogId === ProvidersIdsEnum.doubleGis;

    return isFeatureAvailable && isForwardingBlock && isVisibleForCompany === true;
}

export function PhonesFormItem(props: PropsType): JSX.Element {
    const {catalogId, name, hasInitialPhones, brandId: brandIdProperty} = props;
    const {maxLengthFieldRule, requiredFieldRule} = useFormRules();
    const {getLocalizedString} = useLocale();

    const {getQuery} = useUrl();
    const brandIdFromUrl: number | null = Number(getQuery(urlQueryMap.brandId)) || null;
    const brandId: number | null = brandIdProperty ?? brandIdFromUrl;

    const {data: brandValidationSetting} = useBrandValidationSettings(brandId);

    const isMainPhoneBlock = catalogId === undefined; // eslint-disable-line no-undefined

    // The feature is rolled out brand by brand, so a company outside those brands never asks the
    // backend about the comment in the first place.
    const isFeatureAvailable = usePhoneDescriptionEnabled(brandId ? [brandId] : null);

    // The backend decides whether this company actually pushes to Yandex Maps or 2GIS.
    const {companyId} = useParams<{companyId?: string}>();
    const {data: phoneCommentVisibility} = usePhoneCommentVisibility(
        isFeatureAvailable && companyId ? {companyIds: [Number(companyId)]} : null
    );

    const showPhoneDescription = isPhoneDescriptionShown({
        catalogId,
        isFeatureAvailable,
        isVisibleForCompany: phoneCommentVisibility?.isVisible,
    });

    // eslint-disable-next-line no-undefined
    const initialValue = hasInitialPhones ? undefined : [{phone: '', isMain: true}];

    const phoneLabel = (
        <AdditionalInfo
            leftNode={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__LABEL" />}
            title={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__LABEL" />}
        >
            <Locale
                stringKey={
                    catalogId === ProvidersIdsEnum.yandex
                        ? 'COMPANY_FORM__CONTACT_INFO__PHONE__ADDITIONAL_INFO__YANDEX'
                        : 'COMPANY_FORM__CONTACT_INFO__PHONE__ADDITIONAL_INFO'
                }
            />
        </AdditionalInfo>
    );

    const descriptionLabel = (
        <AdditionalInfo
            leftNode={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__LABEL" />}
            title={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__LABEL" />}
        >
            <Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__ADDITIONAL_INFO" />
        </AdditionalInfo>
    );

    const isPhoneRequired = !brandValidationSetting?.makePhoneOptionalField;

    return (
        <Form.List initialValue={initialValue} name={Array.isArray(name) ? name.slice(1) : name}>
            {(phones, {add, remove}) => {
                const phoneRows = (
                    <div
                        className={classNames(
                            styles.company_phones,
                            showPhoneDescription ? styles.company_phones_with_description : null
                        )}
                    >
                        {phones.map(({key, name: fieldName, fieldKey}, index) => {
                            const rowProps: PhoneRowPropsType = {
                                catalogId,
                                fieldKey,
                                fieldName,
                                getLocalizedString,
                                index,
                                isPhoneRequired,
                                maxLengthFieldRule,
                                phoneLabel,
                                descriptionLabel,
                                requiredFieldRule,
                                onRemove: () => remove(fieldName),
                            };

                            if (showPhoneDescription) {
                                return <Fragment key={key}>{renderPhoneRowWithDescription(rowProps)}</Fragment>;
                            }

                            return <Fragment key={key}>{renderPhoneRowWithoutDescription(rowProps)}</Fragment>;
                        })}
                    </div>
                );

                return (
                    <>
                        {/* The hint explains the comment field, so it only makes sense alongside it. */}
                        {isMainPhoneBlock && showPhoneDescription ? (
                            <div className={styles.phones_block_with_hint}>
                                <div className={styles.phones_block__hint}>
                                    <Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__SENDING_HINT" />
                                </div>

                                {phoneRows}
                            </div>
                        ) : (
                            phoneRows
                        )}

                        <ErrorList name={name} />

                        <AddButton
                            className={styles.company_phones__add}
                            onClick={() =>
                                add(
                                    showPhoneDescription
                                        ? {phone: '', isMain: false, description: ''}
                                        : {phone: '', isMain: false}
                                )
                            }
                            size="small"
                        >
                            <Locale stringKey="BUTTON__ADD" />
                        </AddButton>
                    </>
                );
            }}
        </Form.List>
    );
}
